"use client";
import {
  bottomTextStagger,
  textStaggerChildren,
} from "@/commonComponents/animations";
import EnhancedSwiper from "@/commonComponents/enhanced-swiper";
import { MaterialImage } from "@/commonComponents/material-components";
import { MotionLink } from "@/commonComponents/motion-components";
import SectionWrapper from "@/commonComponents/section-wrapper";
import SwiperNavigationButton from "@/commonComponents/swiper-navigation-button";
import { H6_2, H6_3 } from "@/styles/theme/components/typography.fontvariant";
import { ArrowForwardIos } from "@mui/icons-material";
import {
  Divider,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { StaticImageData } from "next/image";
import { Navigation } from "swiper/modules";

export type ImpactProps = {
  data: {
    title: string;
    description: string;
    swiperData: Array<ImpactSlidesProps>;
    link: string;
    href: string;
  };
};

const Impact = (props: ImpactProps) => {
  const matches = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const { title, description, swiperData, link, href } = props.data;
  return (
    <SectionWrapper
      SectionProps={{
        sx: {
          background: (theme) => theme.palette.gradient.lightToDark,
        },
      }}
      SectionHeaderProps={{
        title: title,
        description: description,
        TitleTypographyProps: {
          variant: "h3",
        },
        DescriptionTypographyProps: {
          fontSize: H6_3,
          maxWidth: 866,
        },
        sx: {
          mb: { xs: 6, md: 10 },
        },
      }}
      navId="story-of-bajaj"
    >
      <EnhancedSwiper
        data={swiperData}
        SlideComponent={ImpactSlides}
        centeredSlides
        initialSlide={1}
        slidesPerView={"auto"}
        spaceBetween={matches ? -15 : 30}
        SlideWrapperProps={{
          sx: {
            width: { xs: 330, md: 423 },
            mt: { xs: "-15px", md: "-50px" },
            // mr: { xs: 0, md: "30px" },
          },
        }}
        navigation={{
          nextEl: ".swiper-impact-next",
          prevEl: ".swiper-impact-prev",
        }}
        sx={{
          overflow: { xs: "visible", md: "hidden" },
          ".swiper-slide": {
            img: {
              opacity: 0.3,
              transform: { xs: "scale(0.8)", md: "scale(0.7)" },
              transition: "all .3s ease-out",
            },
            ".description": {
              opacity: 0,
              transition: "all .3s ease-out",
            },
          },
          ".swiper-slide-active": {
            img: {
              opacity: 1,
              transform: "scale(1)",
            },
            ".description": {
              opacity: 1,
            },
          },
        }}
        modules={[Navigation]}
        Slots={{
          ContainerStartChildren: (
            <SwiperNavigationButton
              PrevButtonProps={{
                className: "swiper-impact-prev",
                sx: {
                  zIndex: 2,
                  display: { xs: "none", md: "flex" },
                },
              }}
              NextButtonProps={{
                className: "swiper-impact-next",
                sx: {
                  ml: "0 !important",
                  zIndex: 2,
                  display: { xs: "none", md: "flex" },
                },
              }}
              alignItems={"center"}
              justifyContent={"space-between"}
              width={1}
              divider={
                <Divider
                  sx={{
                    width: { xs: "113%", md: "calc(100% - 96px)" },
                    ml: { xs: "-20px !important", md: "0 !important" },
                    borderBottom: "2px dashed #ffffff4d",
                  }}
                />
              }
            />
          ),
          ContainerEndChildren: (
            <>
              <SwiperNavigationButton
                display={{ xs: "flex", md: "none" }}
                PrevButtonProps={{
                  className: "swiper-impact-prev",
                }}
                NextButtonProps={{
                  className: "swiper-impact-next",
                }}
              />
              <Divider sx={{ width: 1, borderBottom: "solid 1px #EAEAEA1A" }} />
              <MotionLink
                sx={{
                  fontSize: H6_2,
                  alignSelf: "center",
                }}
                href={href}
                disableRipple
                variant={"text"}
                color={"inherit"}
                variants={textStaggerChildren}
                endIcon={<ArrowForwardIos fontSize="inherit" />}
              >
                {link}
              </MotionLink>
            </>
          ),
          ContainerEndProps: {
            mt: { xs: 3, md: 6 },
            pt: { xs: 3, md: 0 },
            alignItems: "center",
            rowGap: { xs: 3, md: 7 },
            variants: bottomTextStagger(0),
            initial: "initial",
            whileInView: "animate",
            viewport: { once: true },
          },
        }}
      />
    </SectionWrapper>
  );
};

export default Impact;

type ImpactSlidesProps = {
  date: string;
  image: StaticImageData | string;
  description: string;
};

const ImpactSlides = (props: ImpactSlidesProps) => {
  const { date, image, description } = props;
  return (
    <Stack rowGap={{ xs: 4, md: 5.5 }} alignItems={"center"}>
      <Typography
        py={{ xs: 0.75, md: 2 }}
        px={{ xs: 2, md: 3 }}
        bgcolor={"#455675"}
        width={"max-content"}
        variant="body2"
      >
        {date}
      </Typography>
      <Stack gap={2} width={{ xs: 330, md: 423 }} alignItems={"stretch"}>
        <MaterialImage
          src={image}
          alt=""
          width={423}
          height={274}
          sx={{
            width: "100%",
            aspectRatio: 1.54,
            objectFit: "cover",
          }}
        />
        <Typography
          className="description"
          variant="body1"
          textAlign={"center"}
        >
          {description}
        </Typography>
      </Stack>
    </Stack>
  );
};
